import * as $ from "jquery";
import * as ko from "knockout";
import * as Rx from "rxjs";

import * as pli from "../forms";
import { PliCrumbs, PliNavigationPage } from "../forms/forms";
import { init as koGridInit, update as koGridUpdate } from "../ko-grid/knockoutGridBinding";
import { CompositeDisposable } from "../util/disposable";

import { EditGrid } from "./editGrid";


/** Key to use when storing the EditGridController reference on its container element. */
const elementKey = "__kopli__editgrid";

/**
 * Possible states for the PliNavigationPage that contains the edit grid.
 */
enum PageActivationStatus {
    /** Unable to determine activation status because the grid is not associated with a page. */
    Unknown,

    /** Grid container page is not active. */
    Inactive,

    /** Grid container page is active. */
    Active
}

/**
 * Synchronize EditGrid updates with its bound element.
 */
class EditGridController {

    constructor(element: HTMLElement, viewModel: ko.MaybeSubscribable<EditGrid<any, any>>) {
        this.viewModel = ko.unwrap(viewModel);
        if (!(this.viewModel instanceof EditGrid)) { throw new Error("viewModel must be an EditGrid instance"); }

        this.$editGrid = $(".ko-editgrid", element);
        if (this.$editGrid.length !== 1) { throw new Error(`Expected exactly one ".ko-editgrid" element but found ${this.$editGrid.length}`); }

        this.$confirmationPanel = $(".ko-editgrid-confirm", element);
        if (this.$confirmationPanel.length !== 1) { throw new Error(`Expected exactly one ".ko-editgrid-confirm" element but found ${this.$confirmationPanel.length}`); }

        this.$editGridEmpty = $(".ko-editgrid-empty", element);
        if (this.$editGridEmpty.length !== 1) { throw new Error(`Expected exactly one ".ko-editgrid-empty" element but found ${this.$editGridEmpty.length}`); }

        this.$editPanel = $(".ko-editgrid-panel", element);
        if (this.$editPanel.length !== 1) { throw new Error(`Expected exactly one ".ko-editgrid-panel" element but found ${this.$editPanel.length}`); }

        this.viewModel._registerAction("FocusField", this._focusField, this);
        this.viewModel._registerAction("FocusNext", this._focusNext, this);
        this.viewModel._registerAction("MoveNextAndFocus", () => {
            this.viewModel.apply({ action: "MoveNext" });
            this._focusFirst(false);
            this._ensureSelected();
            return false;
        });
        this.viewModel._registerAction("MovePreviousAndFocus", () => {
            this.viewModel.apply({ action: "MovePrevious" });
            this._focusFirst(false);
            this._ensureSelected();
            return false;
        });

        pli.keys.registerKeymap(this.viewModel.id, this.viewModel.keyMap);

        this._disposables = new CompositeDisposable(
            this.viewModel.active.subscribe(this._onActivated, this),
            this.viewModel.dirty.subscribe(this._onDirtyChanged, this),
            this.viewModel.currentItems.subscribe(_ => this._focusFirst(true), this),
            pli.forms.crumbs.subscribe(this._onCrumbsChanging, this, "changing"),
            pli.forms.crumbs.subscribe(this._onCrumbsChanged, this)
        );

        // Find navigation page that contains this grid
        this.page = null;

        const $page = $(element).closest("[data-pli-navigation-page]");
        if ($page.length === 1) {
            this.page = $page.data("pliNavigationPage");
            if (!this.page || this.page.$page[0] !== $page[0]) {
                throw new Error("Invalid Pli navigation page.");
            }
        }
        if (this.page) {
            // Subscribe to pliDeactivating to handle hash changes triggered by
            // "closeForm". This path notifies crumb changes _after_ deactivating
            // the keymap, so we need to deactivate ours in the event handler, which
            // is triggered before the keymap deactivation.
            this._disposables.add(Rx.Observable
                .fromEvent($page, "pliDeactivating")
                .subscribe(e => this.viewModel.apply({ active: false })));
        } else {
            console.warn(`EditGrid "${this.viewModel.id}" not part of navigation page hierarchy; automatic activation/deactivation will be disabled.`);
        }

        // seed with current values
        this._onDirtyChanged(this.viewModel.dirty.peek());
    }

    /**
     * Cleans up the controller binding, removing the viewmodel and disposing subscriptions.
     */
    dispose(): void {
        if (this.viewModel.active.peek()) {
            this.viewModel.apply({ active: false });
        }
        pli.keys.unregisterKeymap(this.viewModel.id);
        this._disposables.dispose();
        this.viewModel = null;
    }

    /** Reference to '.ko-editgrid-confirm' child element. */
    $confirmationPanel: JQuery;

    /** Reference to '.ko-editgrid' child element. */
    $editGrid: JQuery;

    /** Reference to '.ko-editgrid-empty' child element. */
    $editGridEmpty: JQuery;

    /** Reference to '.ko-editgrid-panel' child element. */
    $editPanel: JQuery;

    /** Navigation page containing this grid. */
    page: PliNavigationPage;

    /** EditGrid viewmodel instance bound to the container element. */
    viewModel: EditGrid<any, any>;

    /**
     * If the currently focused field is not selected, select its text.
     */
    private _ensureSelected(): void {
        this.$editPanel.find(":focus").select();
    }

    /**
     * Focuses the nth tabbable input in the edit panel.
     * @param index
     *     Zero-based index of the input to focus.
     * @param e
     *     JQuery key event object.
     * @return
     *     False to prevent event bubbling.
     */
    private _focusField(index: number, e?: JQueryKeyEventObject): boolean {
        if (e) { e.preventDefault(); }
        this.$editPanel.find(":tabbable").eq(index).focus().select();
        return false;
    }

    /**
     * Focuses the first input in the edit panel if current focus is outside the panel and not another input.
     */
    private _focusFirst(respectCurrentFocus = true): void {
        const editPanelHasFocus = this.$editPanel.find(":focus").length > 0;
        const anotherInputHasFocus = document.activeElement && document.activeElement.tagName.toLowerCase() === "input";

        if (!editPanelHasFocus && !(respectCurrentFocus && anotherInputHasFocus)) {
            this._focusField(0);
        }
    }

    /**
     * Focuses the next tabbable input in the edit panel.
     * @param e
     *     JQuery key event object.
     * @return
     *     True if the event should bubble up; false otherwise.
     */
    private _focusNext(e: JQueryKeyEventObject): boolean {
        const focusSinks = this.$editGrid.children("[hidefocus]");
        const focusSink1 = focusSinks.first()[0];
        const focusSink2 = focusSinks.last()[0];

        if (focusSink1 === document.activeElement ||
            focusSink2 === document.activeElement) {
            return this._focusField(0, e);
        } else if ($(document.activeElement).is(":text")) {
            e.preventDefault();
            const activeInput = $(document.activeElement);
            $(document.activeElement).blur();
            const tabbableInputs = this.$editPanel.find(":input:tabbable");
            let nextIndex = tabbableInputs.index(activeInput) + 1;
            if (nextIndex >= tabbableInputs.length) {
                nextIndex = 0;
            }
            tabbableInputs.eq(nextIndex).focus().select();
            return false;
        }

        return true;
    }

    /**
     * Returns the activation status for the grid container page.
     *
     * Grid is considered active if the last crumb page contains this
     * grid or is a child of the grid page and has "keepGridActive": "true".
     */
    private _getPageActivationStatus(crumbs: PliCrumbs): PageActivationStatus {
        if (!this.page) { return PageActivationStatus.Unknown; }

        let crumb = crumbs.end;
        while (crumb) {
            if (this.page === crumb.page) { return PageActivationStatus.Active; }
            if (!crumb.page.keepGridActive) { return PageActivationStatus.Inactive; }
            crumb = crumb.previous;
        }
        return PageActivationStatus.Inactive;
    }

    /*
     * Toggles keymap activation when the grid becomes active/inactive.
     * @param isActive
     *     True if the grid is now active; false if inactive.
     */
    private _onActivated(isActive: boolean): void {
        if (isActive) {
            pli.keys.activateKeymap(this.viewModel.id);
        } else {
            pli.keys.deactivateKeymap(this.viewModel.id);
        }
    }

    /**
     * Deactivates the grid if the new URL will not point to a page containing this grid.
     */
    private _onCrumbsChanging(crumbs: PliCrumbs): void {
        if (this._getPageActivationStatus(crumbs) === PageActivationStatus.Inactive) {
            this.viewModel.apply({ active: false });
        }
    }

    /**
     * Activates the grid if the current URL does point to a page containing this grid.
     */
    private _onCrumbsChanged(crumbs: PliCrumbs): void {
        if (this._getPageActivationStatus(crumbs) === PageActivationStatus.Active) {
            const focusedItem = this.viewModel.focusedItem() || this.viewModel.items()[0];
            const selectedItems: any[] = this.viewModel.selectedItems() || [];
            this.viewModel.apply({
                active: true,
                focusedItem,
                selectedItems
            });
        }
    }

    /**
     * Slide confirmation panel in/out when the dirty state changes.
     */
    private _onDirtyChanged(dirty: boolean): void {
        const translate = (dirty
            ? ["0px", "50px"]
            : ["50px", "0px"]);
        const opacity = (dirty
            ? [1, 0, "linear"]
            : [0, 1, "linear"]);

        this.$confirmationPanel.velocity({
            properties: { translateY: translate, opacity: opacity },
            options: {
                duration: 500,
                easing: "easeOutSine",
                delay: 250
            }
        });
    }

    /** Resources/subscriptions that should be cleaned up when EditGrid is disposed. */
    private _disposables: CompositeDisposable;

}

/**
 * Retrieves the EditGridController instance from the given element.
 */
function requireController(element: HTMLElement): EditGridController {
    const controller = (element && (element as any)[elementKey]);
    if (!controller) { throw new Error("Target element has not been bound using \"editgrid\" binding"); }
    return controller;
}

/**
 * Binding handler 'init' function for EditGrid binding.
 */
export function init(element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<EditGrid<any, any>>): void {
    const viewModel = ko.unwrap(valueAccessor());
    const controller = new EditGridController(element, viewModel);

    koGridInit(controller.$editGrid[0], () => (viewModel as any)._grid);

    // Relocate confirmation panel to inside the grid container to simplify
    // the "slide-in"/"slide-out" animation (uses overflow property of
    // the grid container to obscure panel as it slides up/down).
    // Slickgrid rewrites the contents of the container when it is initialized,
    // so this must be called _after_ koGridInit.
    controller.$editGrid.append(controller.$confirmationPanel.detach());

    (element as any)[elementKey] = controller;
}

/**
 * Binding handler 'update' function for EditGrid binding.
 */
export function update(element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<EditGrid<any, any>>): void {
    const viewModel = ko.unwrap(valueAccessor());
    let controller = requireController(element);

    // Relocate confirmation panel back out of the grid container because
    // 1) the grid *may* be re-initialized in the call to koGridUpdate; and
    // 2) we might create a new EditGridController instance, which expects
    // the panel to be a child of "element"
    $(element).append(controller.$confirmationPanel.detach());

    if (controller.viewModel !== viewModel) {
        controller.dispose();
        controller = new EditGridController(element, viewModel);
        (element as any)[elementKey] = controller;
    }

    koGridUpdate(controller.$editGrid[0], () => (viewModel as any)._grid);

    // Relocate confirmation panel back inside the grid container
    // (see comment in "init").
    controller.$editGrid.append(controller.$confirmationPanel.detach());

    ko.bindingHandlers.visible.update(controller.$editGrid[0], () => ko.pureComputed(() => viewModel.filteredItems().length > 0));
    ko.bindingHandlers.visible.update(controller.$confirmationPanel[0], () => ko.pureComputed(() => viewModel.dirty()));
    ko.bindingHandlers.visible.update(controller.$editGridEmpty[0], () => ko.pureComputed(() => viewModel.filteredItems().length === 0));
    ko.bindingHandlers.visible.update(controller.$editPanel[0], () => ko.pureComputed(() => viewModel.filteredItems().length > 0));
}

declare module "knockout" {
    interface BindingHandlers {
        editgrid: {
            init: (element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<EditGrid<any, any>>) => void,
            update: (element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<EditGrid<any, any>>) => void
        };
    }
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/ko-editgrid/editGridBinding.ts